import {createContext, useCallback, useContext, useEffect, useId, useMemo, useRef, useState, type ReactNode} from 'react';
import {ACCOUNT_DESTINATIONS, ACCOUNT_NAVIGATION, SERVICE_NAVIGATION, WORKSPACE_NAVIGATION, type AccountDestination} from './account-navigation';
import {Brand, ErrorNotice} from './components';
import type {HermesState} from './lib/types';
import type {hermesActions} from './lib/store';
import {APP_BASE, Link} from './router';
import {workspaceContextQuery} from './lib/workspace-location';
import {messageDisplayPrefix, userMessageDisplayText} from './lib/conversation-messages';
import {CreditAllowance} from './workspace-usage';
import {StorageUsage} from './storage-usage';
import {CHAT_NAVIGATION_EVENT, requestChatAction} from './chat-navigation';
import './signed-in-layout.css';

type SidebarSlot = {target: HTMLDivElement | null; onNavigate: () => void};
const SidebarContext = createContext<SidebarSlot | null>(null);
export function useAccountSidebar(): SidebarSlot {
  const context = useContext(SidebarContext);
  if (!context) throw new Error('Workspace navigation requires the signed-in layout.');
  return context;
}
export function NavigationIcon({name}: {name: string}) {
  return <img className="navigation-icon" src={`${APP_BASE}/assets/navigation/${name}.svg`} alt="" width={18} height={18}/>;
}

export function SignedInLayout({state, actions, path, search, title, children}: {
  state: HermesState; actions: typeof hermesActions; path: string; search: string; title?: string | undefined; children: ReactNode;
}) {
  const [narrow, setNarrow] = useState(() => window.matchMedia('(max-width:900px)').matches);
  const [sidebarOpen, setSidebarOpen] = useState(() => !window.matchMedia('(max-width:900px)').matches);
  const [chatSlot, setChatSlot] = useState<HTMLDivElement | null>(null);
  const [query, setQuery] = useState('');
  const sidebar = useRef<HTMLDialogElement>(null), searchDialog = useRef<HTMLDialogElement>(null);
  const searchInput = useRef<HTMLInputElement>(null), toggle = useRef<HTMLButtonElement>(null);
  const header = useRef<HTMLElement>(null);
  const content = useRef<HTMLDivElement>(null);
  const sidebarId = useId();
  const workQuery = workspaceContextQuery(search);
  const chatPage = path === '/workspace';
  const pageTitle = title ?? ACCOUNT_DESTINATIONS.find(item => item.path === path)?.label
    ?? (path === '/subscribe' ? 'Choose your plan' : path === '/setup' ? 'Workspace setup' : path.startsWith('/billing/payments/') ? 'Checkout' : 'Account');
  const destinationHref = (item: AccountDestination) => `${APP_BASE}${item.path}${item.chatContext ? workQuery : ''}`;
  const closeSidebar = useCallback(() => {setSidebarOpen(false); toggle.current?.focus({preventScroll: true});}, []);
  const onNavigate = useCallback(() => {if (narrow) closeSidebar();}, [narrow, closeSidebar]);
  const slot = useMemo(() => ({target: chatSlot, onNavigate}), [chatSlot, onNavigate]);
  const closeMenus = useCallback(() => {
    header.current?.querySelectorAll<HTMLDetailsElement>('details[open]').forEach(menu => {menu.open = false;});
  }, []);
  // Account navigation retains the store's current chat and live transports.
  // Leaving this layout closes them; sign-out also clears all account resources.
  useEffect(() => actions.closeHermes, [actions, state.session.data?.accountId, state.session.data?.csrfToken]);
  useEffect(() => {
    const media = window.matchMedia('(max-width:900px)');
    const change = () => {setNarrow(media.matches); setSidebarOpen(!media.matches);};
    media.addEventListener('change', change);
    return () => media.removeEventListener('change', change);
  }, []);
  useEffect(() => {
    const dialog = sidebar.current;
    if (!dialog) return;
    if (sidebarOpen && !dialog.open) dialog.showModal();
    if (!sidebarOpen && dialog.open) dialog.close();
  }, [sidebarOpen, narrow]);
  useEffect(() => {
    closeMenus(); searchDialog.current?.close(); setQuery(''); onNavigate();
  }, [path, search, closeMenus, onNavigate]);
  useEffect(() => {content.current?.scrollTo({top: 0, behavior: 'instant'});}, [path]);
  useEffect(() => {
    const outside = (event: PointerEvent) => {
      header.current?.querySelectorAll<HTMLDetailsElement>('details[open]').forEach(menu => {
        if (event.target instanceof Node && !menu.contains(event.target)) menu.open = false;
      });
    };
    const showChats = (event: Event) => {if ((event as CustomEvent).detail === 'chats') setSidebarOpen(true);};
    const keyboard = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {event.preventDefault(); openSearch();}
    };
    document.addEventListener('pointerdown', outside);
    window.addEventListener(CHAT_NAVIGATION_EVENT, showChats);
    window.addEventListener('keydown', keyboard);
    return () => {document.removeEventListener('pointerdown', outside); window.removeEventListener(CHAT_NAVIGATION_EVENT, showChats); window.removeEventListener('keydown', keyboard);};
  }, []);
  function openSearch() {
    closeMenus();
    if (!searchDialog.current?.open) searchDialog.current?.showModal();
    searchInput.current?.focus();
  }
  const links = (items: readonly AccountDestination[]) => items.map(item => <Link key={item.path} href={destinationHref(item)}
    className="signed-navigation-link" onClick={onNavigate} aria-current={path === item.path ? 'page' : undefined}>
    <NavigationIcon name={item.icon}/><span>{item.label}</span></Link>);
  const sidebarContent = <>
    <div className="signed-brand"><Brand/>{narrow && <button type="button" className="signed-icon-button" aria-label="Close navigation" onClick={closeSidebar}><NavigationIcon name="x"/></button>}</div>
    <div className="signed-sidebar-scroll">
      <nav aria-label="Workspace navigation">{links(WORKSPACE_NAVIGATION)}</nav>
      <nav aria-label="Services"><h2>Services</h2>{links(SERVICE_NAVIGATION)}</nav>
      <div className="signed-chat-slot" ref={setChatSlot}/>
    </div>
    <nav className="signed-sidebar-bottom" aria-label="Account shortcuts">
      {links(ACCOUNT_NAVIGATION.filter(item => item.path !== '/account/invoices'))}
      <Link className="signed-navigation-link" href={`${APP_BASE}/how-it-works`}><NavigationIcon name="book-open"/><span>Help &amp; guides</span></Link>
    </nav>
  </>;
  const prefix = messageDisplayPrefix(state);
  const matchingPages = ACCOUNT_DESTINATIONS.filter(item => item.label.toLocaleLowerCase().includes(query.trim().toLocaleLowerCase()));
  const matchingChats = prefix === null || !query.trim() ? [] : (state.nativeSessions.data ?? []).filter(chat =>
    userMessageDisplayText(chat.title ?? '', prefix, {folded: true}).toLocaleLowerCase().includes(query.trim().toLocaleLowerCase()));
  const credit = state.accountBalance.data;
  const remaining = credit?.status === 'ready' ? credit.creditBalance.meter.remainingPercent : null;
  const profile = state.accountProfile.data;
  return <SidebarContext.Provider value={slot}><div className={`signed-layout${sidebarOpen ? ' navigation-open' : ' navigation-closed'}${chatPage ? ' signed-chat-layout' : ''}`}>
    {narrow ? <dialog ref={sidebar} id={sidebarId} className="signed-sidebar signed-sidebar-dialog" aria-label="Account navigation"
      onCancel={event => {event.preventDefault(); closeSidebar();}} onClick={event => {
        if (event.target !== event.currentTarget) return;
        const bounds = event.currentTarget.getBoundingClientRect();
        if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) closeSidebar();
      }}>{sidebarContent}</dialog>
      : <aside id={sidebarId} className="signed-sidebar" aria-label="Account navigation" hidden={!sidebarOpen}>{sidebarContent}</aside>}
    <header className="signed-topbar" ref={header} onKeyDown={event => {
      if (event.key !== 'Escape') return;
      const menu = event.target instanceof Element ? event.target.closest('details') : null;
      if (menu?.open) {menu.open = false; menu.querySelector('summary')?.focus();}
    }}>
      <div className="signed-page-location"><button ref={toggle} type="button" className="signed-icon-button" aria-label={sidebarOpen ? 'Hide navigation' : 'Show navigation'}
        aria-controls={sidebarId} aria-expanded={sidebarOpen} onClick={() => setSidebarOpen(value => !value)}><NavigationIcon name="panel-left"/></button>
        <span>{pageTitle}</span></div>
      <button className="signed-search-trigger" type="button" aria-label="Search EightBots" onClick={openSearch}><NavigationIcon name="search"/><span>Search EightBots…</span><kbd aria-hidden="true">Ctrl K</kbd></button>
      <div className="signed-topbar-actions">
        {chatPage && <details className="signed-view-menu"><summary>Chat<NavigationIcon name="chevron-down"/></summary><div className="signed-small-menu">
          {(['conversation', 'terminal', 'whatsapp'] as const).map(view => <button key={view} type="button" onClick={() => {closeMenus(); requestChatAction(view);}}>
            {view === 'conversation' ? 'Chat' : view === 'terminal' ? 'Terminal' : 'WhatsApp'}</button>)}</div></details>}
        <Link className="signed-help-link" href={`${APP_BASE}/how-it-works`}>Help</Link>
        <Link className="signed-icon-button" aria-label="Open files" href={`${APP_BASE}/files${workQuery}`}><NavigationIcon name="folder"/></Link>
        <Link className="signed-icon-button" aria-label="Open messages" href={`${APP_BASE}/messages`}><NavigationIcon name="bell"/></Link>
        <details className="signed-account-menu"><summary aria-label="Account menu" title="Account menu">
          <span className="signed-balance-ring" style={remaining === null ? undefined : {background: `conic-gradient(#333 ${remaining}%, #e9e9e9 0)`}}>
            <span>{remaining === null ? <NavigationIcon name="users"/> : `${Math.round(remaining)}%`}</span></span>
        </summary><div className="signed-account-dropdown">
          <section className="signed-menu-balance"><div><h2>Balance</h2><Link href={`${APP_BASE}/billing`}>View usage</Link></div><CreditAllowance state={state}/></section>
          <section className="signed-menu-profile"><span>Your workspace</span><strong>{profile?.username ?? 'Account'}</strong>
            {credit?.status === 'ready' && credit.allowance && <span>{credit.allowance.planName}</span>}<ErrorNotice error={state.accountProfile.error}/></section>
          <nav aria-label="Account settings">{ACCOUNT_NAVIGATION.map(item => <Link key={item.path} href={destinationHref(item)} onClick={closeMenus}>{item.label}</Link>)}
            {state.session.data?.adminRole && <Link href={`${APP_BASE}/admin`} onClick={closeMenus}>Backoffice</Link>}</nav>
          {state.nativeRuntime.status !== 'idle' && <section className="signed-menu-storage"><StorageUsage state={state} compact/></section>}
          <nav aria-label="Policies"><Link href={`${APP_BASE}/terms`}>Terms of service</Link><Link href={`${APP_BASE}/privacy`}>Privacy policy</Link></nav>
          <button type="button" className="signed-sign-out" disabled={state.operation.label !== null} onClick={() => void actions.signOut().catch(() => undefined)}><NavigationIcon name="log-out"/>Sign out</button>
        </div></details>
      </div>
    </header>
    <div className="signed-content" ref={content}>{children}</div>
    <dialog className="signed-search-dialog" ref={searchDialog} aria-label="Search EightBots" onClick={event => {if (event.target === event.currentTarget) {
      const bounds = event.currentTarget.getBoundingClientRect();
      if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) searchDialog.current?.close();
    }}}>
      <div className="signed-search-field"><NavigationIcon name="search"/><input ref={searchInput} aria-label="Search pages and loaded chats" type="search" value={query} onChange={event => setQuery(event.target.value)} placeholder="Search pages and chats"/>
        <button className="signed-icon-button" type="button" aria-label="Close search" onClick={() => searchDialog.current?.close()}><NavigationIcon name="x"/></button></div>
      <div className="signed-search-results"><h2>Pages</h2>{matchingPages.map(item => <Link key={item.path} href={destinationHref(item)} onClick={() => searchDialog.current?.close()}><NavigationIcon name={item.icon}/>{item.label}</Link>)}
        {matchingChats.length > 0 && <h2>Chats</h2>}{matchingChats.map(chat => <Link key={chat.sessionId} href={`${APP_BASE}/workspace?chat=${encodeURIComponent(chat.sessionId)}`} onClick={() => searchDialog.current?.close()}>
          {userMessageDisplayText(chat.title ?? 'Untitled chat', prefix!, {folded: true})}</Link>)}
        {matchingPages.length === 0 && matchingChats.length === 0 && <p>No matching pages or loaded chats.</p>}
      </div>
    </dialog>
  </div></SidebarContext.Provider>;
}
